import { Box } from '@mui/material'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { CMBCEnhancedPhoneInput } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { PATIENT_FORM_TOKENS as T, PatientFormField } from './PatientFormPrimitives'

/**
 * Restyles react-phone-input-2 (via CMBCEnhancedPhoneInput) to the patient-form control
 * spec: full width, 40px tall, same border / focus ring as text inputs. The `&&` doubles the
 * class so these rules beat the generic EDVCPhoneInput styles regardless of injection order.
 */
const PhoneShell = styled.div`
  width: 100%;
  min-width: 0;
  && .react-tel-input {
    width: 100%;
    max-width: 100%;
    font-family: inherit;
  }
  && .react-tel-input .form-control {
    width: 100%;
    height: ${T.controlHeight}px;
    box-sizing: border-box;
    padding-left: 54px;
    font-size: 14px;
    line-height: ${T.controlHeight - 2}px;
    @media (max-width: 599px) {
      height: ${T.controlHeightMobile}px;
      line-height: ${T.controlHeightMobile - 2}px;
      font-size: 16px;
    }
    color: ${T.text};
    background: ${T.surface};
    border: 1px solid ${T.border};
    border-radius: ${T.radius}px;
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  }
  && .react-tel-input .form-control::placeholder {
    color: #94a3b8;
    opacity: 1;
  }
  && .react-tel-input .form-control:hover:not(:disabled) {
    border-color: ${T.borderHover};
  }
  && .react-tel-input .form-control:focus {
    border-color: ${T.accent};
    box-shadow: 0 0 0 3px ${T.accentRing};
    outline: none;
  }
  && .react-tel-input .form-control:disabled {
    background: ${T.disabledBg};
    color: ${T.disabledText};
    border-color: #e2e8f0;
    cursor: default;
  }
  && .react-tel-input .flag-dropdown {
    border: 1px solid ${T.border};
    border-right: none;
    border-radius: ${T.radius}px 0 0 ${T.radius}px;
    background: ${T.surfaceSoft};
    transition: border-color 0.15s ease;
  }
  && .react-tel-input .flag-dropdown.open,
  && .react-tel-input .flag-dropdown.open .selected-flag,
  && .react-tel-input .selected-flag:hover,
  && .react-tel-input .selected-flag:focus {
    background: #eef2ff;
    border-radius: ${T.radius}px 0 0 ${T.radius}px;
  }
  && .react-tel-input .selected-flag {
    width: 46px;
    padding-left: 11px;
    border-radius: ${T.radius}px 0 0 ${T.radius}px;
  }
  && .react-tel-input .country-list {
    margin-top: 6px;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
    font-size: 13px;
  }
  && .react-tel-input .country-list .country.highlight,
  && .react-tel-input .country-list .country:hover {
    background: #eef2ff;
  }
  .patient-field--required-empty && .react-tel-input .form-control:not(:focus) {
    background: #fff5f5;
    border-color: #fca5a5;
  }
  .patient-field--required-empty && .react-tel-input .flag-dropdown {
    border-color: #fca5a5;
  }
  &[data-invalid='true'] .react-tel-input .form-control,
  &[data-invalid='true'] .react-tel-input .flag-dropdown {
    border-color: ${T.error};
  }
  &[data-invalid='true'] .react-tel-input .form-control:focus {
    box-shadow: 0 0 0 3px ${T.errorSoft};
  }
`

/**
 * Phone number field with a single feedback line: an explicit `error` from form validation
 * wins, then the libphonenumber error once the field was touched, then the formatted number
 * as a quiet confirmation.
 */
export default function PatientPhoneField({
  label,
  required = false,
  span = { xs: 12, md: 6 },
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  disabled = false,
  placeholder,
  error,
  touched = false,
  inputProps,
  overlay,
  footer,
  labelAddon,
  hideLabel = false,
  compact = false,
  requiredEmpty = false,
}) {
  const [phoneState, setPhoneState] = useState({ isValid: false, error: null, formattedNumber: null })
  const handleValidationStateChange = useCallback((state) => setPhoneState(state), [])

  const libraryError = touched && !phoneState.isValid ? phoneState.error : null
  const shownError = error || libraryError
  const successHint =
    !shownError && phoneState.isValid && phoneState.formattedNumber ? phoneState.formattedNumber : null

  const control = (
    <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <PhoneShell data-invalid={shownError ? 'true' : undefined}>
        <CMBCEnhancedPhoneInput
          inputProps={inputProps}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          fullWidth
          hideValidationFeedback
          onValidationStateChange={handleValidationStateChange}
        />
      </PhoneShell>
      {overlay}
    </Box>
  )

  if (compact) {
    return control
  }

  return (
    <PatientFormField
      label={hideLabel ? null : label}
      required={required}
      span={span}
      error={shownError}
      hint={successHint}
      hintTone="success"
      footer={footer}
      labelAddon={labelAddon}
      requiredEmpty={requiredEmpty}
    >
      {control}
    </PatientFormField>
  )
}

export { PhoneShell as PatientPhoneShell }
